import {
  SidePanelDS,
  TextCapitalized,
  Spacer,
  triggerToast,
  SelectDS,
} from "@qivia/ui";
import { useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { VehiclesListType } from "../vehiclesAPI";
import {
  selectVehicleDepartmentUpdatedStatus,
  updateVehicleDepartmentAsync,
  vehiclesSlice,
} from "../vehiclesSlice";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import {
  departmentsSlice,
  getDepartmentsByCompanyAsync,
  selectDepartments,
  selectGetDepartmentsByCompanyStatus,
} from "../../departments/departmentsSlice";
import { selectCompany } from "../../homeSlice";
import { Option } from "@qivia/ui/src/designSystem/components/Select";

export const DepartmentSection = (props: {
  department: string | null;
  vehicleUuid: string;
  vehiclesList: VehiclesListType[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const departmentsList = useAppSelector(selectDepartments);
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );

  const vehicleDepartmentUpdatedStatus = useAppSelector(
    selectVehicleDepartmentUpdatedStatus,
  );

  const currentDepartment = useMemo(() => {
    return departmentsList.find((department) => department.uuid === props.department);
  }, [departmentsList, props.department]);

  const departmentOptions = useMemo(() => {
    return departmentsList.map((department) => ({
      value: department.uuid,
      label: department.name,
    }));
  }, [departmentsList]);

  const updateVehicleDepartment = useCallback(( selectedOption: Option<string> | null ) => {
    if (!company || !selectedOption) return;

    const departmentName = typeof selectedOption.label === "string" ? selectedOption.label : null;

    if (!departmentName) return;

    void dispatch(updateVehicleDepartmentAsync({
      vehicleUuid: props.vehicleUuid,
      departmentName: departmentName,
      companyUuid: company.uuid,
    }));
  }, [company, dispatch, props.vehicleUuid]);

  const departmentValueOption = useMemo(() => {
    return departmentOptions.find((department) => department.value === currentDepartment?.uuid) ?? null;
  }, [currentDepartment?.uuid, departmentOptions]);

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [company, dispatch, getDepartmentsByCompanyStatus]);

  useEffect(() => {
    if (vehicleDepartmentUpdatedStatus === "success") {
      void dispatch(departmentsSlice.actions.resetDepartmentsListStatus());
      void dispatch(vehiclesSlice.actions.resetVehicleUpdatedDepartmentStatus());
      triggerToast(t("vehicles.updatedDepartment.success") || "", "valid");
    } else if (vehicleDepartmentUpdatedStatus === "failed") {
      triggerToast(t("vehicles.updatedDepartment.failure") || "", "error");
      dispatch(vehiclesSlice.actions.resetVehicleUpdatedDepartmentStatus());
    }
  }, [vehicleDepartmentUpdatedStatus, t, company, dispatch]);

  if (!company) {
    return;
  }

  return (
    <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
      <SectionTitle>
        <TextCapitalized>
          {t("vehicles.sidepanel.department.title")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={1} />
      <StyledFlex>
      <SelectDS
          label={""}
          placeholder={t("vehicles.sidepanel.department.placeholder") || ""}
          value={departmentValueOption}
          options={departmentOptions}
          onChange={(selectedOption) => updateVehicleDepartment(selectedOption)}
          emptyErrorMessage={t("vehicles.sidepanel.department.emptyErrorMessage") || ""}
          withoutSpacerBottom
          allWidth
      />
      </StyledFlex>
    </SidePanelDS.Section>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledFlex = styled.div`
  display: flex;
`;
